import React, { useState } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import classNames from 'classnames/bind';
import { graphql, useStaticQuery } from 'gatsby';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Heading from 'components/shared/heading';
import Button from 'components/shared/button';
import PlayIcon from 'icons/play.inline.svg';
import QuoteIcon from 'icons/quote.inline.svg';
import dotPattern from './svg/dot-pattern.svg';

import styles from './testimonials.module.scss';

const cx = classNames.bind(styles);

const quoteAnimationVariants = {
  initial: {
    y: 108,
    opacity: 0,
  },
  appear: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: [0.21, 0.49, 0.33, 1] },
  },
};

const Testimonials = () => {
  const [isVideoStartedPlaying, setIsVideoStartedPlaying] = useState(false);
  const [sectionRef, inView] = useInView({ threshold: 0.5, triggerOnce: true });

  const {
    wpSharedBlock: {
      acf: { title, testimonials },
    },
    videoBackground,
  } = useStaticQuery(graphql`
    query {
      wpSharedBlock(slug: { eq: "testimonials" }) {
        acf {
          title
          testimonials {
            avatar {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 60)
                }
              }
              altText
            }
            name
            position
            text
            videoFile {
              guid
            }
          }
        }
      }
      videoBackground: file(relativePath: { eq: "shared/testimonials/video-background.jpg" }) {
        childImageSharp {
          gatsbyImageData(width: 780)
        }
      }
    }
  `);

  const {
    avatar,
    text,
    name,
    position,
    videoFile: { guid: videoUrl },
  } = testimonials[0];

  const handlePlayButtonClick = () => setIsVideoStartedPlaying(true);

  return (
    <section className={cx('wrapper', { cardIsHidden: isVideoStartedPlaying })} ref={sectionRef}>
      <div className="container">
        <Heading className={cx('title')} tag="h2" size="xl">
          {title}
        </Heading>

        <div className={cx('video-wrapper')}>
          {!isVideoStartedPlaying && (
            <motion.div
              className={cx('quote-wrapper')}
              variants={quoteAnimationVariants}
              animate={inView ? 'appear' : 'initial'}
            >
              <figure className={cx('quote')}>
                <QuoteIcon className={cx('quote-icon')} />
                <blockquote className={cx('quote-text')}>
                  <p>{text}</p>
                </blockquote>
                <figcaption className={cx('quote-info')}>
                  <GatsbyImage
                    className={cx('quote-avatar')}
                    image={getImage(avatar.localFile.childImageSharp)}
                    alt={avatar.localFile.altText || name}
                  />
                  <div>
                    <h3 className={cx('quote-name')}>{name}</h3>
                    <span className={cx('quote-position')}>{position}</span>
                  </div>
                </figcaption>
              </figure>
            </motion.div>
          )}

          <GatsbyImage
            className={cx('video-background')}
            image={getImage(videoBackground)}
            alt=""
          />

          {!isVideoStartedPlaying && (
            <Button className={cx('play-button')} theme="tertiary" onClick={handlePlayButtonClick}>
              <PlayIcon />
            </Button>
          )}

          {isVideoStartedPlaying && (
            // eslint-disable-next-line jsx-a11y/media-has-caption -- testimonial clip has no caption track available
            <video className={cx('video')} autoPlay controls loop playsInline>
              <source src={videoUrl} type="video/mp4" />
            </video>
          )}

          <img className={cx('dot-pattern')} src={dotPattern} alt="" loading="lazy" aria-hidden />
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
